import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, Lock, User, Mail, Phone, ShieldCheck, KeyRound, Eye, EyeOff, Check } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AuthShell } from "../components/AuthShell";
import { AuthTabs } from "../components/AuthTabs";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, verify2FA } = useAuth();
  const [form, setForm] = useState({ username: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  // Prefill username from `?u=<username>` URL param — used by the QR code on
  // the one-click signup PNG so the user only has to type their password.
  // If `?p=<password>&auto=1` are also present, we auto-submit for a truly
  // one-tap QR login. Password from the QR image is passed transiently and
  // scrubbed from the URL bar right after submission.
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  useEffect(() => {
    const u = (searchParams.get("u") || "").trim();
    const p = searchParams.get("p") || "";
    const auto = searchParams.get("auto") === "1";
    if (u) {
      setForm((s) => (s.username ? s : { ...s, username: u, password: p || s.password }));
    } else if (p) {
      setForm((s) => ({ ...s, password: p }));
    }
    if (u && p && auto) {
      setAutoLoggingIn(true);
      // Scrub the query params from the URL so the password isn't sitting in
      // browser history / analytics after the auto-login runs.
      try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
      (async () => {
        try {
          const res = await login(u, p, { remember: true });
          if (res.ok && res.requires_2fa) {
            setChallenge({ challenge_token: res.challenge_token, username: res.username });
            toast.info("Enter the 6-digit code from your authenticator app");
          } else if (res.ok) {
            toast.success("Signed in via QR");
            navigate("/dashboard", { replace: true });
          } else {
            toast.error(res.error || "Auto login failed — please sign in manually");
            setError(res.error || "Auto login failed");
          }
        } finally {
          setAutoLoggingIn(false);
        }
      })();
    }
  }, []);

  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // {challenge_token, username}
  const [otp, setOtp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // Smart identifier detection — drives the field icon + label.
  const identKind = useMemo(() => {
    const v = (form.username || "").trim();
    if (!v) return "any";
    if (v.includes("@")) return "email";
    if (/^[+\-\s\d]+$/.test(v) && /\d/.test(v)) return "phone";
    return "username";
  }, [form.username]);

  const IdentIcon = identKind === "email" ? Mail : identKind === "phone" ? Phone : User;
  const identLabel =
    identKind === "email"
      ? "Email"
      : identKind === "phone"
      ? "Phone Number"
      : identKind === "username"
      ? "Username"
      : "Username, Phone Number, Email";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.username || !form.password) {
      setError("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    const res = await login(form.username, form.password, { remember });
    setSubmitting(false);
    if (res.ok && res.requires_2fa) {
      setChallenge({ challenge_token: res.challenge_token, username: res.username });
      toast.info("Enter the 6-digit code from your authenticator app");
      return;
    }
    if (res.ok) {
      toast.success("Welcome back!");
      const target = location.state?.from?.pathname || "/dashboard";
      navigate(target, { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  const onVerify2FA = async (e) => {
    e.preventDefault();
    setError("");
    const code = useBackup ? backupCode.trim().toUpperCase() : otp.trim();
    if (!code) {
      setError(useBackup ? "Enter a backup code" : "Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    const res = await verify2FA({
      challenge_token: challenge.challenge_token,
      totp_code: useBackup ? "" : code,
      backup_code: useBackup ? code : "",
    });
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back!");
      const target = location.state?.from?.pathname || "/dashboard";
      navigate(target, { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  const inputCls =
    "w-full h-12 rounded-lg px-4 bg-[#1a2030] text-white placeholder:text-white/35 border border-white/5 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/30 transition-all";

  // ----- 2FA challenge view -----
  if (challenge) {
    return (
      <AuthShell variant="copper">
        <div className="space-y-5" data-testid="login-2fa-form">
          <div className="text-center space-y-2">
            <div className="inline-flex h-14 w-14 rounded-2xl items-center justify-center bg-emerald-500/10 border border-emerald-500/30 shadow-[0_8px_24px_rgba(25,165,92,0.25)]">
              <ShieldCheck className="text-emerald-400" size={28} />
            </div>
            <h2 className="font-display text-2xl font-extrabold text-white">Two-factor authentication</h2>
            <p className="text-sm text-white/60">
              Hi <span className="text-emerald-300 font-semibold">{challenge.username}</span> — enter the 6-digit code from your authenticator app{useBackup ? " (or paste a backup code)" : ""}.
            </p>
          </div>

          <form onSubmit={onVerify2FA} className="space-y-4">
            {!useBackup ? (
              <input
                data-testid="login-2fa-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="000000"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`}
                autoFocus
              />
            ) : (
              <input
                data-testid="login-2fa-backup"
                type="text"
                placeholder="XXXXX-XXXXX"
                maxLength={11}
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                className={`${inputCls} text-center text-lg tracking-widest font-mono uppercase`}
                autoFocus
              />
            )}

            <button
              type="button"
              onClick={() => { setUseBackup((v) => !v); setOtp(""); setBackupCode(""); setError(""); }}
              className="w-full inline-flex items-center justify-center gap-2 text-sm text-emerald-300/85 hover:text-emerald-200 font-semibold"
              data-testid="login-2fa-toggle-backup"
            >
              <KeyRound size={14} />
              {useBackup ? "Use authenticator code instead" : "Use a backup code"}
            </button>

            {error && (
              <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-2fa-submit"
              className="w-full h-12 rounded-lg bg-[#19a55c] hover:bg-[#21c46e] text-white font-bold text-lg flex items-center justify-center disabled:opacity-60 shadow-[0_8px_24px_rgba(25,165,92,0.45)]"
            >
              {submitting ? <Loader2 className="animate-spin" size={20} /> : "Verify & continue"}
            </button>

            <button
              type="button"
              onClick={() => { setChallenge(null); setOtp(""); setBackupCode(""); setError(""); }}
              className="w-full text-xs text-white/45 hover:text-white/70"
            >
              ← Use a different account
            </button>
          </form>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell variant="copper">
      <AuthTabs active="login" />

      {/* Auto-login overlay (QR scan flow) */}
      {autoLoggingIn && (
        <div
          data-testid="login-qr-auto-overlay"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md animate-[fadeIn_.15s_ease-out]"
        >
          <div className="bg-[#0d1420] border border-[#19a55c]/40 rounded-2xl px-8 py-7 flex flex-col items-center gap-3 shadow-[0_25px_80px_rgba(0,0,0,0.6)] max-w-[300px]">
            <Loader2 className="animate-spin text-[#21c46e]" size={32} />
            <div className="text-white font-black text-base">Signing you in…</div>
            <div className="text-white/55 text-[12.5px] text-center leading-snug">
              Verifying your QR credentials, one moment.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm sm:text-base text-white/90 font-medium" htmlFor="username">
            <IdentIcon size={14} className="text-[#19a55c] transition-all" data-testid="login-ident-icon" />
            <span data-testid="login-ident-label">{identLabel}</span>
          </label>
          <input
            id="username"
            data-testid="login-username-input"
            type="text"
            autoComplete="username"
            placeholder="Enter username, phone number or email"
            value={form.username}
            onChange={onChange("username")}
            className={inputCls}
          />
          <p className="text-[11px] text-white/45 leading-relaxed">
            Email login works only after your email is verified.
          </p>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm sm:text-base text-white/90 font-medium" htmlFor="password">
            <Lock size={14} className="text-[#19a55c]" /> Password
          </label>
          <div className="relative">
            <input
              id="password"
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={form.password}
              onChange={onChange("password")}
              className={`${inputCls} pr-12`}
            />
            <button
              type="button"
              data-testid="login-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-[#19a55c] transition-colors p-1"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor="login-remember"
            className="flex items-center gap-2 text-sm text-white/75 hover:text-white cursor-pointer select-none"
            data-testid="login-remember-label"
          >
            <span className="relative inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center">
              <input
                id="login-remember"
                data-testid="login-remember-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded border border-white/25 bg-[#1a2030] checked:border-[#19a55c] checked:bg-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/40 transition-colors"
              />
              <Check
                size={12}
                strokeWidth={3}
                className="pointer-events-none relative text-white opacity-0 peer-checked:opacity-100 transition-opacity"
              />
            </span>
            Remember me for 30 days
          </label>
          <Link
            to="/forgot-password"
            data-testid="forgot-password-link"
            className="text-[#19a55c] underline underline-offset-2 hover:text-[#21c46e] text-sm font-semibold"
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <div role="alert" data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-btn"
          className="w-full h-12 rounded-lg bg-[#19a55c] hover:bg-[#21c46e] active:bg-[#168f4f] text-white font-bold text-lg transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(25,165,92,0.45)]"
        >
          {submitting ? <Loader2 className="animate-spin" size={20} /> : "Log in"}
        </button>
      </form>
    </AuthShell>
  );
}
